<template>
  <div v-show="showDownloadManager">
    <q-card square flat bordered class="my-card bg-grey-1 vertical-bottom">
      <q-toolbar>
        <q-toolbar-title class="text-weight-bold text-grey-brand"
          >Download Manager</q-toolbar-title
        >
        <q-btn
          flat
          round
          dense
          icon="close"
          @click="onDownloadManagerCloseClick"
        />
      </q-toolbar>

      <!-- Download type selection. -->
      <q-card-section>
        <div class="column">
          <q-select
            v-model="selectedDownloadType"
            dense
            color="blue-brand"
            outlined
            :options="downloadTypeOptions"
            label="Select Download Type"
            @update:model-value="onDownloadTypeSelect"
          />
        </div>
      </q-card-section>

      <!-- Completed pig run download controls. -->
      <q-card-section v-show="showDownloadTypeCompletedPigRun">
        <div class="column">
          <div v-if="completedPigRuns.length > 0">
            <q-select
              v-model="selectedCompletedPigRun"
              dense
              color="blue-brand"
              outlined
              clearable
              :options="completedPigRunOptions"
              label="Select Completed Pig Run"
              popup-content-style="white-space: normal; word-break: break-word;"
              @update:model-value="onCompletedPigRunSelect"
              @clear="onCompletedPigRunClear"
            >
              <template #selected-item="scope">
                <span
                  style="
                    white-space: normal;
                    word-break: break-word;
                    line-height: 1.3;
                  "
                >
                  {{ scope.opt.label }}
                </span>
              </template>
            </q-select>
            <PigRunSummary
              ref="pigRunSummary"
              :pig-run-id="selectedCompletedPigRunId"
              @loaded="onPigRunSummaryLoaded"
            />
            <br />
            <q-btn
              class="btn-brand"
              label="Download Recorded Passage Data"
              dense
              no-caps
              :disable="!selectedCompletedPigRun"
              @click="onCompletedPigRunDownloadRecordedPassageDataClick"
            />
          </div>
          <div v-else>There are no completed pig runs to download from.</div>
        </div>
      </q-card-section>

      <!-- By AGM(s) download controls. -->
      <q-card-section v-show="showDownloadTypeDevice">
        <div class="column">
          <q-btn
            class="btn-brand"
            label="Enter download parameters"
            dense
            no-caps
            @click="onEnterParametersForDevicesDownloadClick"
          />

          <!-- Parameter summary -->
          <div
            v-if="hasDevicesDownloadParameters"
            class="q-mt-sm q-pa-sm"
            style="
              background: #f5f5f5;
              border-radius: 4px;
              font-size: 12px;
              line-height: 1.4;
            "
          >
            <div class="text-bold q-mb-xs">Parameters</div>
            <div>
              <span class="text-grey-7">Start:</span>
              {{ startDateDevicesDownload }}
            </div>
            <div>
              <span class="text-grey-7">End:</span>
              {{ endDateDevicesDownload }}
            </div>
            <div class="q-mt-xs">
              <span class="text-grey-7"
                >AGMs ({{ selectedDevicesForDevicesDownload.length }}):</span
              >
              {{ truncatedDeviceLabelsForDevicesDownload }}
              <a
                v-if="selectedDevicesForDevicesDownload.length > 5"
                href="#"
                style="font-size: 11px"
                @click.prevent="showAllAgmsSummary = !showAllAgmsSummary"
              >
                {{ showAllAgmsSummary ? 'Show less' : 'Show all' }}
              </a>
            </div>
          </div>

          <br />
          <q-btn
            class="btn-brand"
            label="Download Recorded Passage Data"
            dense
            no-caps
            @click="onDevicesDownloadRecordedPassageDataClick"
          />
        </div>
      </q-card-section>

      <!-- EN-1516: queued commands for the AGMs in my group, and the sessions panel. -->
      <q-separator />
      <q-card-section>
        <QueuedCommandsList
          :active="showDownloadManager"
          :can-show-sessions="canShowDownloadSessions"
          @show-sessions="onShowDownloadSessionsClick"
        />
        <!-- Only when EN-1517's openDownloadSessions is registered (merge
             order: EN-1515, EN-1516, then EN-1517). -->
        <q-btn
          v-if="canShowDownloadSessions"
          class="btn-brand q-mt-sm"
          label="Show download sessions"
          dense
          no-caps
          data-test="show-download-sessions"
          @click="onShowDownloadSessionsClick()"
        >
          <q-badge
            v-if="downloadSessionCountBadge !== null"
            color="white"
            text-color="blue-brand"
            floating
            :label="downloadSessionCountBadge"
          />
        </q-btn>
      </q-card-section>
    </q-card>

    <!-- Enter parameters for devices download dialog. -->
    <q-dialog v-model="showEnterParametersForDevicesDownloadDialog">
      <q-card style="width: 600px; max-width: 90vm">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Download Parameters</q-toolbar-title
          >
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="column">
            <div class="col q-pb-sm">
              <!-- Start date/time -->
              <q-input
                v-model="startDateDevicesDownload"
                dense
                filled
                label="Start Date (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qStartDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="startDateDevicesDownload"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @update:model-value="hideStartDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qStartTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="startDateDevicesDownload"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        @update:model-value="hideStartTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>

              <!-- End date/time -->
              <q-input
                v-model="endDateDevicesDownload"
                dense
                filled
                label="End Date (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qEndDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="endDateDevicesDownload"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @update:model-value="hideEndDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qEndTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="endDateDevicesDownload"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        @update:model-value="hideEndTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
          </div>
          <q-separator />

          <div class="column" style="max-height: 300px; overflow-y: auto">
            <div class="col q-pb-sm">
              <q-option-group
                v-model="selectedDevicesForDevicesDownload"
                dense
                :options="allDeviceOptionsForDevicesDownload"
                color="indigo-9"
                type="checkbox"
              />
            </div>
          </div>

          <q-separator />

          <q-card-actions align="right">
            <q-btn
              class="btn-brand"
              label="Set Parameters"
              type="button"
              no-caps
              @click="onSetParametersForDevicesDownloadClick"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Validation message dialog. -->
    <q-dialog v-model="showValidationMessageDialog">
      <q-card>
        <q-card-section>
          <div class="text-h6">{{ validationDialogTitle }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">{{
          validationDialogMessage
        }}</q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="OK" color="primary" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { hidePopupProxy } from '@/utils/QuasarProxyHelper';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PigRunService from '@/services/PigRunService';
import DateCommon from '@/utils/DateCommon';
import {
  DownloadRangeDefault,
  refreshedDownloadRange,
} from '@/utils/DownloadRangeDefaults';
import { sortByUidAscending } from '@/utils/UidSort';
import { Device } from '@/models/Device';
import { DownloadRecordedPassageDataRequest } from '@/models/ReportManager/DownloadRecordedPassageDataRequest';
import { ActivePigRun } from '@/models/ActivePigRun';
import PigRunSummary from '@/components/PigRunSummary.vue';
import QueuedCommandsList from '@/components/QueuedCommandsList.vue';
import {
  OPEN_DOWNLOAD_SESSIONS_ACTION,
  downloadSessionCountBadge,
  hasStoreAction,
} from '@/utils/QueuedCommandDisplay';
import {
  DOWNLOAD_NOT_ALLOWED_TITLE,
  downloadRequestForbiddenMessage,
} from '@/utils/DownloadRequestErrors';

dayjs.extend(utc);

export default defineComponent({
  name: 'DownloadManager',
  components: {
    PigRunSummary,
    QueuedCommandsList,
  },
  data() {
    return {
      selectedDownloadType: '',
      showDownloadTypeCompletedPigRun: false,
      showDownloadTypeDevice: false,
      // Completed pig run
      selectedCompletedPigRun: '',
      cachedPigRunData: null as any,
      // Devices download
      showEnterParametersForDevicesDownloadDialog: false,
      startDateDevicesDownload: '',
      startDateDevicesDownloadAsDate: undefined as Date | undefined,
      endDateDevicesDownload: '',
      endDateDevicesDownloadAsDate: undefined as Date | undefined,
      // EN-1507: the last default range filled in, to tell it from user edits.
      lastDefaultDevicesDownloadRange: null as DownloadRangeDefault | null,
      selectedDevicesForDevicesDownload: [] as any[],
      showAllAgmsSummary: false,
      // Validation
      showValidationMessageDialog: false,
      validationDialogTitle: '',
      validationDialogMessage: '',
      // Pig runs list
      pigRunsList: [] as ActivePigRun[],
      isPigRunsListLoading: false,
    };
  },
  computed: {
    showDownloadManager(): boolean {
      return this.$store.state.showDownloadManager;
    },
    downloadTypeOptions(): any[] {
      return [
        { label: 'By Completed Pig Run', value: 'CompletedPigRun' },
        { label: 'By Agm(s)', value: 'Device' },
      ];
    },
    completedPigRuns(): any[] {
      return this.pigRunsList.filter((pr: any) => pr.lifecycleState === 'D');
    },
    completedPigRunOptions(): any[] {
      return this.completedPigRuns.map((pr: any) => ({
        value: pr.pigRunId,
        runData: pr,
        label:
          (pr.receiveDate
            ? dayjs.default(pr.receiveDate).utc().format('YYYY-MM-DD HH:mm')
            : 'N/A') +
          ' / ' +
          pr.name,
      }));
    },
    allDeviceOptionsForDevicesDownload(): any[] {
      const devices: Device[] = this.$store.state.device.devices || [];
      return sortByUidAscending(
        devices.map((device) => ({
          label: 'AGM UID ' + device.uid,
          value: device.uid,
        })),
        (option) => option.value
      );
    },
    hasDevicesDownloadParameters(): boolean {
      return (
        this.startDateDevicesDownload !== '' &&
        this.endDateDevicesDownload !== '' &&
        this.selectedDevicesForDevicesDownload.length > 0
      );
    },
    truncatedDeviceLabelsForDevicesDownload(): string {
      const labels = this.selectedDevicesForDevicesDownload.map(
        (uid: number) => {
          const opt = this.allDeviceOptionsForDevicesDownload.find(
            (o: any) => o.value === uid
          );
          return opt ? opt.label : 'UID ' + uid;
        }
      );
      if (!this.showAllAgmsSummary && labels.length > 5) {
        return labels.slice(0, 5).join(', ') + ' ...';
      }
      return labels.join(', ');
    },
    selectedCompletedPigRunId(): number | undefined {
      const value = (this.selectedCompletedPigRun as any)?.value;
      return typeof value === 'number' ? value : undefined;
    },
    // EN-1516: true once EN-1517's openDownloadSessions action exists.
    canShowDownloadSessions(): boolean {
      return hasStoreAction(this.$store, OPEN_DOWNLOAD_SESSIONS_ACTION);
    },
    // EN-1516: badge from Chad's EN-1517 `downloadSessionCounts` getter
    // (null, so no badge, until that getter is on main).
    downloadSessionCountBadge(): number | null {
      return downloadSessionCountBadge(
        this.$store.getters.downloadSessionCounts
      );
    },
  },
  watch: {
    showDownloadManager(visible: boolean) {
      if (visible) {
        this.fetchPigRunsList();
      } else {
        (this.$refs.pigRunSummary as any)?.removeMapPreview();
        this.$store.commit('SET_PLOTTED_PIG_RUN_ID', null);
      }
    },
  },
  async mounted() {
    await this.fetchPigRunsList();
  },
  methods: {
    onDownloadManagerCloseClick() {
      this.$store.dispatch('closeManagerPanel');
    },
    // EN-1516: opens Chad's EN-1517 download sessions panel, optionally on
    // one AGM / session / command (a queued row's eye button passes these).
    onShowDownloadSessionsClick(payload?: {
      uid?: number;
      downloadSessionId?: number;
      commandId?: number;
    }) {
      if (!this.canShowDownloadSessions) return;
      this.$store.dispatch(OPEN_DOWNLOAD_SESSIONS_ACTION, {
        ...(payload || {}),
        refresh: true,
      });
    },
    onDownloadTypeSelect() {
      this.showDownloadTypeDevice =
        this.getSelectedDownloadTypeValue() === 'Device';
      this.showDownloadTypeCompletedPigRun =
        this.getSelectedDownloadTypeValue() === 'CompletedPigRun';
    },
    getSelectedDownloadTypeValue(): string {
      return (this.selectedDownloadType as any)?.value || '';
    },
    getSelectedCompletedPigRunId(): number | null {
      const value = (this.selectedCompletedPigRun as any)?.value;
      return typeof value === 'number' ? value : null;
    },
    onCompletedPigRunSelect() {
      this.cachedPigRunData = null;
    },
    onCompletedPigRunClear() {
      this.selectedCompletedPigRun = '';
      this.cachedPigRunData = null;
    },
    onPigRunSummaryLoaded(data: any) {
      this.cachedPigRunData = data;
    },
    showServiceException(
      logId: any,
      title = 'Download Manager Service Exception'
    ) {
      this.validationDialogMessage =
        'A download manager service exception has occurred. LogId = [' +
        logId +
        ']';
      this.validationDialogTitle = title;
      this.showValidationMessageDialog = true;
    },
    handleException(exception: any, title?: string): boolean {
      if (exception?.response?.data !== undefined) {
        this.showServiceException(exception.response.data, title);
      }
      return false;
    },
    onEnterParametersForDevicesDownloadClick() {
      // EN-1507: fill a fresh now-30min..now (UTC) when the fields are empty
      // or still hold the last default we filled; keep a range the user entered.
      const range = refreshedDownloadRange(
        {
          start: this.startDateDevicesDownload,
          end: this.endDateDevicesDownload,
        },
        this.lastDefaultDevicesDownloadRange,
        new Date()
      );
      if (range) {
        this.startDateDevicesDownload = range.start;
        this.endDateDevicesDownload = range.end;
        this.lastDefaultDevicesDownloadRange = range;
      }
      this.showEnterParametersForDevicesDownloadDialog = true;
    },
    validateDevicesParameters(
      dialogTitle = 'Invalid download parameter.'
    ): boolean {
      if (!DateCommon.IsDateAsStringValid(this.startDateDevicesDownload)) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage = 'The start date is not valid.';
        this.showValidationMessageDialog = true;
        return false;
      }
      if (!DateCommon.IsDateAsStringValid(this.endDateDevicesDownload)) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage = 'The end date is not valid.';
        this.showValidationMessageDialog = true;
        return false;
      }
      this.startDateDevicesDownloadAsDate =
        DateCommon.ConvertDateAsStringToDate(this.startDateDevicesDownload);
      this.endDateDevicesDownloadAsDate = DateCommon.ConvertDateAsStringToDate(
        this.endDateDevicesDownload
      );
      if (
        this.startDateDevicesDownloadAsDate.getTime() >=
        this.endDateDevicesDownloadAsDate.getTime()
      ) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage =
          'The start date must be before the end date.';
        this.showValidationMessageDialog = true;
        return false;
      }
      if (this.selectedDevicesForDevicesDownload.length === 0) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage =
          'At least one uid value must be selected.';
        this.showValidationMessageDialog = true;
        return false;
      }
      return true;
    },
    onSetParametersForDevicesDownloadClick() {
      if (this.validateDevicesParameters()) {
        this.showEnterParametersForDevicesDownloadDialog = false;
      }
    },
    async validateDownloadFirmware(
      request: DownloadRecordedPassageDataRequest
    ): Promise<boolean> {
      try {
        const firmwareResponse =
          await PigRunService.validateDownloadRecordedPassageDataRequestFirmware(
            { uidList: request.uidList }
          );
        if (!firmwareResponse.data?.isFirmwareValidForRequest) {
          this.validationDialogTitle =
            'Download Recorded Passage Data Validation';
          this.validationDialogMessage =
            'The firmware for the AGM(s) [' +
            (firmwareResponse.data.uidList || []).join(', ') +
            '] must be upgraded to download recorded passage data.';
          this.showValidationMessageDialog = true;
          return false;
        }
      } catch (exception: any) {
        this.handleException(exception);
        return false;
      }
      return true;
    },
    async startDownloadSession(request: DownloadRecordedPassageDataRequest) {
      try {
        await PigRunService.downloadRecordedPassageDataRequest(request);
        for (const uid of request.uidList) {
          this.$store.dispatch('createPendingDownloadSession', {
            uid,
            fileType: 0,
            startDate: request.launchDate.toISOString(),
            endDate: request.receiveDate.toISOString(),
          });
        }
      } catch (exception: any) {
        // EN-1518: the API refuses AGMs outside the caller's group with a 403.
        const forbidden = downloadRequestForbiddenMessage(exception);
        if (forbidden !== null) {
          this.validationDialogTitle = DOWNLOAD_NOT_ALLOWED_TITLE;
          this.validationDialogMessage = forbidden;
          this.showValidationMessageDialog = true;
          return;
        }
        this.handleException(exception);
      }
    },
    async onCompletedPigRunDownloadRecordedPassageDataClick() {
      const selectedPigRunId = this.getSelectedCompletedPigRunId();
      if (selectedPigRunId === null) {
        this.validationDialogMessage = 'A completed pig run must be selected.';
        this.validationDialogTitle = 'Information Message';
        this.showValidationMessageDialog = true;
        return;
      }

      let uidArray: number[] = [];
      let launchDate: Date | null = null;
      let receiveDate: Date | null = null;

      let run = this.cachedPigRunData;
      if (!run) {
        try {
          const response = await PigRunService.getPigRun(selectedPigRunId);
          run = response.data || {};
        } catch (exception: any) {
          this.handleException(exception);
          return;
        }
      }

      uidArray = Array.isArray(run.devices)
        ? run.devices.map((d: any) => d.uid)
        : [];
      launchDate = run.launchDate ? new Date(run.launchDate) : null;
      receiveDate = run.receiveDate ? new Date(run.receiveDate) : null;

      if (!uidArray.length || !launchDate || !receiveDate) {
        this.validationDialogTitle =
          'Download Recorded Passage Data Validation';
        this.validationDialogMessage =
          'The selected completed pig run does not have valid AGM/date data.';
        this.showValidationMessageDialog = true;
        return;
      }

      const request = new DownloadRecordedPassageDataRequest(
        launchDate,
        receiveDate,
        uidArray
      );
      const isValid = await this.validateDownloadFirmware(request);
      if (isValid) {
        await this.startDownloadSession(request);
      }
    },
    async onDevicesDownloadRecordedPassageDataClick() {
      if (
        !this.validateDevicesParameters(
          'Download Recorded Passage Data Validation'
        )
      ) {
        return;
      }
      const request = new DownloadRecordedPassageDataRequest(
        this.startDateDevicesDownloadAsDate as Date,
        this.endDateDevicesDownloadAsDate as Date,
        [...this.selectedDevicesForDevicesDownload]
      );
      const isValid = await this.validateDownloadFirmware(request);
      if (isValid) {
        await this.startDownloadSession(request);
      }
    },
    async fetchPigRunsList() {
      if (this.isPigRunsListLoading) return;
      this.isPigRunsListLoading = true;
      try {
        const response = await PigRunService.getPigRuns();
        this.pigRunsList = response.data;
      } catch (error) {
        console.error('Failed to fetch pig runs list:', error);
      } finally {
        this.isPigRunsListLoading = false;
      }
    },
    hideStartDateProxy() {
      hidePopupProxy(this, 'qStartDateProxy');
    },
    hideStartTimeProxy() {
      hidePopupProxy(this, 'qStartTimeProxy');
    },
    hideEndDateProxy() {
      hidePopupProxy(this, 'qEndDateProxy');
    },
    hideEndTimeProxy() {
      hidePopupProxy(this, 'qEndTimeProxy');
    },
  },
});
</script>
